import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicWebSubjects } from '../../service/publicService';
import { mediaEndPoint } from '../../service/instace';
import { CircularProgress } from '@mui/material';

export default function LandingPage() {
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const res = await getPublicWebSubjects();
      if (res?.status === 'success') {
        setSubjects(res.message);
      }
    } catch (error) {
      console.error('Failed to fetch public subjects', error);
    } finally {
      setLoading(false);
    }
  };

  const subjectsWithCourses = subjects.filter(subject => subject.course && subject.course.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      {/* Navigation Bar */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate('/')}>
              {/* Replace with your actual logo */}
              <span className="text-2xl font-black text-blue-600 dark:text-blue-400 tracking-tighter">
                LMS<span className="text-gray-800 dark:text-white">Portal</span>
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/login')}
                className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
              >
                Log In
              </button>
              <button 
                onClick={() => navigate('/student/register')}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="relative bg-blue-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <svg className="h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100">
            <polygon fill="currentColor" points="0,100 100,0 100,100" />
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 flex flex-col items-center text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl mb-6">
            Unlock Your Potential with Our Courses
          </h1>
          <p className="text-xl max-w-3xl mb-10 text-blue-100">
            Discover a wide variety of subjects tailored to help you succeed. 
            Learn from the best and achieve your goals today.
          </p>
          <button 
            onClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-100 transition duration-300"
          >
            Explore Courses
          </button>
        </div>
      </section>

      {/* Main Content */}
      <main id="courses-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress size={60} />
          </div>
        ) : subjectsWithCourses.length === 0 ? (
          <div className="text-center py-20">
            <h2 className="text-2xl font-semibold text-gray-600 dark:text-gray-400">No courses available at the moment.</h2>
          </div>
        ) : (
          subjectsWithCourses.map((subject, idx) => (
            <div key={subject.id || idx} className="mb-16">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  {subject.name}
                </h2>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                  {subject.course?.length || 0} Courses
                </span>
              </div>
              
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                  {subject.course.map((course: any) => (
                    <div 
                      key={course.id} 
                      className="group bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700"
                    >
                      <div className="relative aspect-video overflow-hidden bg-gray-200 dark:bg-gray-700">
                        {course.image ? (
                          <img 
                            src={course.image} 
                            alt={course.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/400x225?text=No+Image' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            No Image
                          </div>
                        )}
                        <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold text-green-600 dark:text-green-400 shadow-sm">
                          ₹{course.amount}
                        </div>
                      </div>
                      
                      <div className="p-6 flex flex-col flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                          {course.name}
                        </h3>
                        {/* Assuming duration is in days or months. If months, you might format differently */}
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          {course.duration} Days Duration
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-6 flex-grow" dangerouslySetInnerHTML={{ __html: course.description || '' }} />
                        
                        <button 
                          onClick={() => navigate('/login')}
                          className="w-full bg-blue-50 dark:bg-gray-700 text-blue-600 dark:text-blue-400 font-semibold py-3 rounded-xl group-hover:bg-blue-600 group-hover:text-white dark:group-hover:bg-blue-500 transition-colors duration-300"
                        >
                          View Course Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}
